import * as angular from 'angular';
import {IHttpService, IPromise, IQService} from 'angular';
import {AccountModel, SchedulingProjectConfigurations} from "../angular/models/account.model";
import {SplitService} from "../../commons/services/split-service.service";
import {$accountFactory} from "../$account.factory";
import {$accountsFactory} from "../$accounts.factory";
import {BringozApi, BringozApiAction} from "../../persistence-layer/models/bringoz-api.model";
import {ApiConfig} from "../../commons/models/api-config.model";
import {ExportAccountsPayloadModel} from "../models/export-accounts-payload.model";
import {AddressQualityEnum} from "../../addresses/types/address-quality.enum";
import {
  AccountsWithPreferredAgentsResponse,
  GetAccountsWithPreferredAgentsParams,
  UpsertMultipleAccountPreferredAgentsRequest
} from "../models/account-with-preferred-agents.model";
import {MetaDataModel} from "../../commons/models/meta-data.model";

export class AccountRequests {

  /* @ngInject */
  constructor(private $account: $accountFactory, private $accounts: $accountsFactory,
              private accountsAPI: BringozApi, private $q: IQService,
              private splitService: SplitService, private Upload,
              private apiConfig: ApiConfig, private $http: IHttpService) {
  }

  getAccountPricePlanBundleUser(accountId: string) {
    const api = this.accountsAPI.actions.getAccountPricePlanBundleUser;
    return this.objectCall(accountId, api, {}, {}, {}, false);
  }

  setAccountPricePlanBundle(accountId: string, bundleId: string): IPromise<any> {
    const api = this.accountsAPI.actions.setAccountPricePlanBundle;
    return this.objectCall(accountId, api, {}, {}, {
      bundleId: bundleId
    }, false);
  }

  addUserToAccount(accountId: string, invitedUser) {
    const api = this.accountsAPI.actions.addUserToAccount;
    return this.objectCall(accountId, api, {}, invitedUser, {}, false);
  }

  getPreferences(accountId: string) {
    const api = this.accountsAPI.actions.getPreferences;
    return this.objectCall(accountId, api, {}, {}, {}, false);
  }

  updatePreferences(accountId: string, payload) {
    const api = this.accountsAPI.actions.updatePreferences;
    return this.objectCall(accountId, api, {}, payload, {}, false);
  }

  createOne(account: AccountModel): IPromise<AccountModel> {
    const api = this.accountsAPI.actions.createOne;
    return this.$callList(api, {}, account, {}) as IPromise<AccountModel>;
  }

  updateOne(accountId: string, account: AccountModel): IPromise<AccountModel> {
    const api = this.accountsAPI.actions.updateOne;
    return this.objectCall(accountId, api, {}, account, {}) as IPromise<AccountModel>;
  }

  deleteAccount(accountId: string, shouldMask: boolean) {
    const api = this.accountsAPI.actions.deleteAccount;
    return this.objectCall(accountId, api, {mask: shouldMask}, {}, {});
  }

  addDepotToAccounts(depotId: string, accountIds: string[], excludeIds: string[]): IPromise<AccountModel[]> {
    const api = this.accountsAPI.actions.addDepotToAccounts;
    return this.$callList(api, {
      depotId: depotId,
      accountIds: accountIds,
      excludedIds: excludeIds
    }, {}, {}) as IPromise<AccountModel[]>;
  }

  getTotalAccounts(branchIds: string[], archive: boolean, type, tags?: string[],
                   addressQualityGroups?: AddressQualityEnum[], resolvingTypes?: string[]) {
    const api = this.accountsAPI.actions.getTotalAccounts;
    return this.$callList(api, {type: type, branchIds: branchIds, archive: archive, tags: tags,
      addressQualityGroups: addressQualityGroups, resolvingTypes: resolvingTypes}, {}, {});
  }

  updateAccountProfile(profile, accountId: string) {
    const api = this.accountsAPI.actions.updateAccountProfile;
    return this.$callList(api, {accountId: accountId}, profile, {});
  }

  removeContract(accountId: string) {
    const api = this.accountsAPI.actions.removeContract;
    return this.$callList(api, {}, {}, {accountId: accountId}) as IPromise<AccountModel>;
  }

  exportAccounts(payload: ExportAccountsPayloadModel, branchIds, accountType: string, tags?: string[], isActive?: boolean): Promise<AccountModel> {
    const api = this.accountsAPI.actions.exportAccounts;
    const params: any = {branchIds: branchIds};
    if (isActive !== undefined) {
      params.isActive = isActive;
    }
    return this.$callList(api, params, payload, {}) as Promise<AccountModel>;
  }

  uploadAccounts(file, customSchemaId?: string) {
    const siteId = this.splitService.getSiteId();
    return this.Upload.upload({
      url: this.apiConfig.baseUrl + `/importers-accounts/validate`,
      method: 'POST',
      params: {siteId: siteId, customSchemaId: customSchemaId},
      data: {file: file}
    })
      .then((response) => {
        return this.$q.when(response.data);
      })
  }

  unArchive(accountId: string) {
    const api = this.accountsAPI.actions.unArchive;
    return this.$callList(api, {}, {accountId: accountId}, {}) as IPromise<any>;
  }

  addBranches(accountIds: string[], branchIds: string[]) {
    const api = this.accountsAPI.actions.addBranches;
    return this.$callList(api, {}, {accountIds: accountIds, branchIds: branchIds}, {}) as IPromise<any>;
  }


  getAll(queryParams) {
    const api = this.accountsAPI.actions.getAll;
    return this.$callList(api, queryParams, {}, {});
  }

  setSchedulingProjectConfigurations(accountId: string, payload: SchedulingProjectConfigurations) {
    const api = this.accountsAPI.actions.setSchedulingProjectConfigurations;
    return this.$callList(api, {}, payload, {id: accountId});
  }

  getPreferredAgents(accountId: string, params?: {
    siteId?: string,
    id?: string[],
    agentId?: string[],
    serviceType?: string[],
    page?: number,
    size?: number,
    sort?: string
  }) {
    const api = this.accountsAPI.actions.getPreferredAgents;
    const queryParams = {
      siteId: params?.siteId,
      id: params?.id,
      agentId: params?.agentId,
      serviceType: params?.serviceType,
      page: params?.page || 0,
      size: params?.size || 10,
      sort: params?.sort || 'priority,desc'
    };
    return this.$callList(api, queryParams, {}, {accountId: accountId});
  }

  createPreferredAgent(accountId: string, agentData: any) {
    const api = this.accountsAPI.actions.createPreferredAgent;
    return this.$callList(api, {}, agentData, {accountId: accountId});
  }

  deletePreferredAgents(accountId: string, agentIds: string[]) {
    const api = this.accountsAPI.actions.deletePreferredAgents;
    return this.$callList(api, {
      id: agentIds
    }, {}, {accountId: accountId});
  }

  updatePreferredAgents(accountId: string, agents: any[]) {
    const api = this.accountsAPI.actions.updatePreferredAgents;
    return this.$callList(api, {}, agents, {accountId: accountId});
  }

  getRestrictedAgents(accountId: string, params?: {
    id?: string[],
    agentId?: string[],
    serviceType?: string[],
    page?: number,
    size?: number,
    sort?: string
  }) {
    const api = this.accountsAPI.actions.getRestrictedAgents;
    const queryParams = {
      id: params?.id,
      agentId: params?.agentId,
      serviceType: params?.serviceType,
      page: params?.page || 0,
      size: params?.size || 10,
      sort: params?.sort || 'priority,desc'
    };
    return this.$callList(api, queryParams, {}, {accountId: accountId});
  }

  createRestrictedAgent(accountId: string, agentData: any) {
    const api = this.accountsAPI.actions.createRestrictedAgent;
    return this.$callList(api, {}, agentData, {accountId: accountId});
  }

  updateRestrictedAgents(accountId: string, agents: any[]) {
    const api = this.accountsAPI.actions.updateRestrictedAgents;
    return this.$callList(api, {}, agents, {accountId: accountId});
  }

  deleteRestrictedAgents(accountId: string, agentIds: string[]) {
    const api = this.accountsAPI.actions.deleteRestrictedAgents;
    return this.$callList(api, {
      id: agentIds
    }, {}, {accountId: accountId});
  }

  getDedicatedAgents(accountId: string, params?: {
    id?: string[],
    agentId?: string[],
    serviceType?: string[],
    page?: number,
    size?: number,
    sort?: string
  }) {
    const api = this.accountsAPI.actions.getDedicatedAgents;
    const queryParams = {
      id: params?.id,
      agentId: params?.agentId,
      serviceType: params?.serviceType,
      page: params?.page || 0,
      size: params?.size || 10,
      sort: params?.sort || 'priority,desc'
    };
    return this.$callList(api, queryParams, {}, {accountId: accountId});
  }

  createDedicatedAgent(accountId: string, agentData: any) {
    const api = this.accountsAPI.actions.createDedicatedAgent;
    return this.$callList(api, {}, agentData, {accountId: accountId});
  }

  updateDedicatedAgents(accountId: string, agents: any[]) {
    const api = this.accountsAPI.actions.updateDedicatedAgents;
    return this.$callList(api, {}, agents, {accountId: accountId});
  }

  deleteDedicatedAgents(accountId: string, agentIds: string[]) {
    const api = this.accountsAPI.actions.deleteDedicatedAgents;
    return this.$callList(api, {
      id: agentIds
    }, {}, {accountId: accountId});
  }

  getAccountsWithPreferredAgents(params: GetAccountsWithPreferredAgentsParams, useCursorEndpoint?: boolean) {
    const endpoint = useCursorEndpoint ? 'preferredAgents/cursor' : 'preferredAgents';
    const queryParams: any = {
      siteId: this.splitService.getSiteId(),
      branchIds: params.branchIds,
      depotIds: params.depotIds,
      agentIds: params.agentIds,
      tags: params.tags,
      segment: params.segment,
      query: params.query,
      ids: params.ids,
      cursor: params.cursor,
      limit: params.limit || 100,
      sort: params.sort || 'createdAt,desc',
      isActive: params.isActive,
      isArchive: params.isArchive
    };

    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined || queryParams[key] === null) {
        delete queryParams[key];
      }
    });

    return this.$http.get<AccountsWithPreferredAgentsResponse>(
      `${this.apiConfig.baseUrl}/accounts/${endpoint}`,
      {params: queryParams}
    ).then((response) => response.data);
  }

  assignMultiplePreferredAgents(accountId: string, payload: UpsertMultipleAccountPreferredAgentsRequest) {
    const api = this.accountsAPI.actions.assignMultiplePreferredAgents;
    return this.$callList(api, {}, payload, {accountId: accountId});
  }

  updateMetaData(accountId: string, metaData: MetaDataModel[]) {
    const api = this.accountsAPI.actions.updateMetaData;
    return this.$callList(api, {}, metaData, {accountId: accountId});
  }

  queryAccounts(body: {[key: string]: any}): IPromise<{cursor: string, list: AccountModel[]}> {
    const {branchIds, sort, ...bodyPayload} = body;
    const params: any = {siteId: this.splitService.getSiteId()};
    if (branchIds?.length) {
      params.branchIds = branchIds;
    }
    if (sort) {
      params.sort = sort;
    }
    return this.$http.post<{cursor: string, list: AccountModel[]}>(
      `${this.apiConfig.baseUrl}/accounts/query`, bodyPayload, {params}
    ).then((response) => response.data);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private objectCall(accountId: string, apiCall: BringozApiAction, params: any, data: any, options: any, doUpdate?: boolean) {
    return this.$account(accountId).$call(apiCall, params, data, options, doUpdate);
  }

  private $callList(apiCall: BringozApiAction, params: any, data: any, options: any) {
    return this.$accounts().$call(apiCall, params, data, options);
  }

}

angular
  .module('bringoz.admin.accounts')
  .service('accountRequests', AccountRequests);
